import { readErrorMessage } from '@Components/util/utils'
import { FraudServiceAPI } from '@API/data-services/fraud-service'
import { RootState } from '@AppState/redux-store'
import { Modal } from '@Components/common/modal'
import { Pagination } from '@Components/common/pagination'
import { usePaginatedState } from '@Components/common/use-paginated-state'
import { Routes } from '@Components/main-app/types'
import {
  AdminPlayerRestriction,
  AuthScopes,
  PlayerRestrictionState,
  RuleType,
} from '@Generated/back-office-client/src'
import {
  Badge,
  Button,
  Container,
  Flex,
  Heading,
  Link,
  Section,
  Skeleton,
  Table,
  Text,
  TextArea,
  TextField,
} from '@radix-ui/themes'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { formatInTimeZone } from 'date-fns-tz'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import { Link as RouterLink } from 'react-router-dom'

const PAGE_SIZE = 20

const formatRuleType = (ruleType: RuleType): string => {
  switch (ruleType) {
    case RuleType.VELOCITY:
      return 'Scan Velocity'
    case RuleType.INVALID_COUNT:
      return 'Invalid Scan Count'
    case RuleType.MANUAL:
      return 'Manual Block'
    default:
      return ruleType
  }
}

export const FraudDashboard = () => {
  const queryClient = useQueryClient()
  const { activeCustomerId, activeCustomerMetadata, currentUser } = useSelector(
    (state: RootState) => state.users,
  )
  const timezone = activeCustomerMetadata?.timezone ?? 'UTC'
  const canWrite = !!currentUser?.scopes.includes(AuthScopes.writefraud_controls)

  const [page, setPage] = usePaginatedState()
  const [blockModalOpen, setBlockModalOpen] = useState(false)
  const [blockPlayerId, setBlockPlayerId] = useState('')
  const [blockNote, setBlockNote] = useState('')
  const [unblockTarget, setUnblockTarget] = useState<AdminPlayerRestriction | null>(null)
  const [unblockNote, setUnblockNote] = useState('')
  const [actionError, setActionError] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['fraud-restrictions', activeCustomerId, page],
    queryFn: () => FraudServiceAPI.getRestrictions(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
  })

  const closeModals = () => {
    setBlockModalOpen(false)
    setUnblockTarget(null)
    setBlockPlayerId('')
    setBlockNote('')
    setUnblockNote('')
    setActionError(null)
  }

  const { mutate: blockPlayer, isPending: isBlocking } = useMutation({
    mutationFn: () => FraudServiceAPI.blockPlayer(blockPlayerId.trim(), blockNote.trim()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fraud-restrictions', activeCustomerId] })
      closeModals()
    },
    onError: async (error) => {
      setActionError(await readErrorMessage(error, 'Failed to block player'))
    },
  })

  const { mutate: unblockPlayer, isPending: isUnblocking } = useMutation({
    mutationFn: () => FraudServiceAPI.overrideRestriction(unblockTarget!.id, unblockNote.trim()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fraud-restrictions', activeCustomerId] })
      closeModals()
    },
    onError: async (error) => {
      setActionError(await readErrorMessage(error, 'Failed to unblock player'))
    },
  })

  const formatExpiry = (expiry: Date | null | undefined): string => {
    if (!expiry) {
      return 'Indefinite'
    }
    return formatInTimeZone(expiry, timezone, 'PPp')
  }

  const restrictions = data?.restrictions ?? []

  return (
    <Container>
      <Section>
        <Flex direction="column" gap="3">
          <Flex justify="between" align="center">
            <Heading as="h1" size="5">
              Fraud Dashboard
            </Heading>
            {canWrite ? (
              <Button
                onClick={() => {
                  setActionError(null)
                  setBlockModalOpen(true)
                }}>
                Block Player
              </Button>
            ) : null}
          </Flex>
          <Text size="2" color="gray">
            Players currently blocked from scanning or within a probation window.
          </Text>

          <Skeleton loading={isLoading}>
            {restrictions.length || isLoading ? (
              <Table.Root className="results-table">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeaderCell>Player ID</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Triggered By</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Tier</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Applied At</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Block Expires</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Probation Expires</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Note</Table.ColumnHeaderCell>
                    {canWrite ? <Table.ColumnHeaderCell></Table.ColumnHeaderCell> : null}
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {restrictions.map((restriction) => (
                    <Table.Row key={restriction.id}>
                      <Table.RowHeaderCell>
                        <Link asChild>
                          <RouterLink
                            to={Routes.PLAYER_DETAILS.replace(
                              ':lookupId',
                              restriction.playerLookupId,
                            )}>
                            {restriction.playerLookupId}
                          </RouterLink>
                        </Link>
                      </Table.RowHeaderCell>
                      <Table.Cell>
                        <Badge
                          color={
                            restriction.restrictionState === PlayerRestrictionState.BLOCKED
                              ? 'red'
                              : 'amber'
                          }>
                          {restriction.restrictionState === PlayerRestrictionState.BLOCKED
                            ? 'Blocked'
                            : 'Probation'}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell>{formatRuleType(restriction.ruleType)}</Table.Cell>
                      <Table.Cell>
                        {restriction.ruleType === RuleType.MANUAL
                          ? '—'
                          : restriction.penaltyTier + 1}
                      </Table.Cell>
                      <Table.Cell>
                        {formatInTimeZone(restriction.appliedAt, timezone, 'PPp')}
                      </Table.Cell>
                      <Table.Cell>{formatExpiry(restriction.lockoutExpiresAt)}</Table.Cell>
                      <Table.Cell>{formatExpiry(restriction.probationExpiresAt)}</Table.Cell>
                      <Table.Cell>{restriction.applyNote ?? 'N/A'}</Table.Cell>
                      {canWrite ? (
                        <Table.Cell>
                          <Button
                            size="1"
                            variant="soft"
                            color="red"
                            onClick={() => {
                              setActionError(null)
                              setUnblockTarget(restriction)
                            }}>
                            Unblock
                          </Button>
                        </Table.Cell>
                      ) : null}
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            ) : (
              <Flex>No players are currently blocked or on probation.</Flex>
            )}
          </Skeleton>
          {data?.totalResults ? (
            <Pagination
              totalItems={data.totalResults}
              pageSize={PAGE_SIZE}
              currentPage={page}
              onPageChange={setPage}
            />
          ) : null}
        </Flex>
      </Section>

      {blockModalOpen ? (
        <Modal
          onOpenChange={(open) => {
            if (!open) {
              closeModals()
            }
          }}
          title="Block Player"
          description="Manually block a player from scanning indefinitely"
          hideTitle={false}>
          <Flex direction="column" gap="3">
            <Flex direction="column" gap="1">
              <Text size="2" weight="medium">
                Player ID
              </Text>
              <TextField.Root
                value={blockPlayerId}
                placeholder="Player lookup ID"
                onChange={(e) => setBlockPlayerId(e.target.value)}
              />
            </Flex>
            <Flex direction="column" gap="1">
              <Text size="2" weight="medium">
                Note
              </Text>
              <TextArea
                value={blockNote}
                placeholder="Reason for the block"
                onChange={(e) => setBlockNote(e.target.value)}
              />
            </Flex>
            {actionError ? <Text color="red">{actionError}</Text> : null}
            <Flex gap="3" justify="end">
              <Button variant="soft" color="gray" onClick={closeModals}>
                Cancel
              </Button>
              <Button
                color="red"
                disabled={!blockPlayerId.trim() || !blockNote.trim() || isBlocking}
                loading={isBlocking}
                onClick={() => blockPlayer()}>
                Block Player
              </Button>
            </Flex>
          </Flex>
        </Modal>
      ) : null}

      {unblockTarget ? (
        <Modal
          onOpenChange={(open) => {
            if (!open) {
              closeModals()
            }
          }}
          title={`Unblock ${unblockTarget.playerLookupId}`}
          description="Lift the player's active restriction"
          hideTitle={false}>
          <Flex direction="column" gap="3">
            <Flex direction="column" gap="1">
              <Text size="2" weight="medium">
                Note
              </Text>
              <TextArea
                value={unblockNote}
                placeholder="Reason for lifting the restriction"
                onChange={(e) => setUnblockNote(e.target.value)}
              />
            </Flex>
            {actionError ? <Text color="red">{actionError}</Text> : null}
            <Flex gap="3" justify="end">
              <Button variant="soft" color="gray" onClick={closeModals}>
                Cancel
              </Button>
              <Button
                disabled={!unblockNote.trim() || isUnblocking}
                loading={isUnblocking}
                onClick={() => unblockPlayer()}>
                Unblock Player
              </Button>
            </Flex>
          </Flex>
        </Modal>
      ) : null}
    </Container>
  )
}
